import React, { FC, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Channel } from 'pusher-js';
import { isBefore } from 'date-fns';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import {
  AccountTypes,
  ButtonClassNames,
  GlobalRouteNames,
  LocalStorageItems,
  QueryParams,
  ServiceCardIconType,
  WsEvents,
} from '../../../models/Common';
import { userInfoApi } from '../../../services/apiServices/user/userInfo';
import { useAppDispatch } from '../../../hooks/redux';
import { userInfoSlice } from '../../../store/reducers/UserInfoSlice';
import { formatAccountType, getCardIconType, parseJwt, handleTopUpBalanceBankCardAnalytics } from '../../../utils';
import { CtvAccount, InternetAccount, IptvAccount, UnionAccount } from '../../../models/Accounts';
import { toDay, CustomerOrderStatuses } from '../../../constants/configuration';
import { TextButton, TextButtonClassNames } from '../../commons/buttons/TextButton';
import { paymentsApi } from '../../../services/apiServices/user/payments';
import Wss from '../../../core/pusher/Pusher';
import { OrderProcessing } from '../OrderProcessing';
import { AccountIcon } from '../../commons/icons/AccountIcon';
import DateVO from '../../../core/valueObjects/DateVO';
import { BasicTable } from '../../commons/customTables/BasicTable';
import { TableBodyCell } from '../../../models/CustomTable';

interface accountsToActivate {
  name: string;
  price: number | string;
  isDaily: boolean;
  iconType: ServiceCardIconType;
  handleActivate: (id: string | number, type: string) => void;
  accountId: string | number;
  accountType: AccountTypes;
}

export const Payment: FC = () => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(0);
  const [processing, setProcessing] = useState<boolean>();
  const channelRef = useRef<Channel | null>(null);
  const channelNameRef = useRef<string | null>(null);
  const { search } = useLocation();
  const history = useHistory();
  const [fetchOrderInfo] = paymentsApi.useLazyGetOrdersInfoQuery();
  const [accountsToActivate, setAccountsToActivate] = useState<accountsToActivate[]>([]);
  const { data: connectedAccounts, isLoading } = userInfoApi.useGetConnectedAccountsQuery('', {
    skip: processing !== false,
  });
  const { updateUserBalance } = userInfoSlice.actions;
  const dispatch = useAppDispatch();
  const topUpAmount = localStorage.getItem(LocalStorageItems.TopUpAmount);
  const topUpBalance = localStorage.getItem(LocalStorageItems.TopUpBalance);

  const listenPaymentComplete = () => {
    const token = localStorage.getItem(LocalStorageItems.AccessToken);
    const { sub } = parseJwt(token);
    const pusher = Wss.getInstance();

    const channelName = `customer.${sub}`;
    const channel = pusher.subscribe(channelName);

    const cleanupChannel = () => {
      channel.unbind_all();
      pusher.unsubscribe(channelName);

      channelRef.current = null;
      channelNameRef.current = null;
    };

    channel.bind(WsEvents.PaymentComplete, (data) => {
      setAmount(data.amount);
      dispatch(updateUserBalance(data));
      setProcessing(false);
      cleanupChannel();
    });

    channel.bind(WsEvents.PaymentError, (data) => {
      cleanupChannel();
      history.push({
        pathname: GlobalRouteNames.UnknownError,
        state: { message: data.failureReason },
      });
    });

    channelRef.current = channel;
    channelNameRef.current = channelName;
  };

  const handleOrderInfo = async (orderCode: string, isInitialLoad = false) => {
    const { data } = await fetchOrderInfo(orderCode, false);

    if (!data?.length) {
      history.push(GlobalRouteNames.UnknownError);
      return;
    }
    const order = data[0];

    if (order.Status === CustomerOrderStatuses.ERROR) {
      history.push({
        pathname: GlobalRouteNames.UnknownError,
        state: { message: order.FailureReason },
      });
      return;
    }
    if (isInitialLoad) {
      if (order.Status !== CustomerOrderStatuses.PENDING) {
        setAmount(order.Amount);
        setProcessing(false);
      }
      return;
    }
    if (order.Status === CustomerOrderStatuses.PROCESSED) {
      setAmount(order.Amount);
      setProcessing(false);
      return;
    }
    history.push(GlobalRouteNames.UnknownError);
  };

  const onLoad = async () => {
    if (!topUpAmount) {
      history.push(GlobalRouteNames.HomePage);
      return;
    }
    if (topUpBalance) {
      handleTopUpBalanceBankCardAnalytics(topUpAmount);
    }
    const queryParams = new URLSearchParams(search);
    const orderCode = queryParams.get('orderCode');
    if (orderCode) {
      setProcessing(true);
      listenPaymentComplete();
      await handleOrderInfo(orderCode, true);
    }
  };

  useEffect(() => {
    let timerId;

    if (processing) {
      timerId = setTimeout(async () => {
        const queryParams = new URLSearchParams(search);
        const orderCode = queryParams.get('orderCode');
        if (orderCode) {
          await handleOrderInfo(orderCode);
        } else {
          history.push(GlobalRouteNames.UnknownError);
        }
      }, 15000);
    }
    if (processing === false) {
      localStorage.removeItem(LocalStorageItems.TopUpAmount);
    }
    return () => clearTimeout(timerId);
  }, [processing]);

  const handleActivate = (id: string | number, accountType: string) => {
    history.push({
      pathname: GlobalRouteNames.ServicePayment,
      search: `?${QueryParams.AccountId}=${id}&${QueryParams.AccountType}=${formatAccountType(accountType)}`,
    });
  };

  const filterNotActivatedAccounts = (
    accounts: CtvAccount[] | IptvAccount[] | UnionAccount[] | InternetAccount[] | null,
  ) => {
    if (accounts) {
      return Array.prototype.filter.call(accounts, (item) => {
        const realExpireDate = DateVO.create(item.RealExpireDate.toString()).get();

        const isActive = item.StatusID === 1;
        const isDisabled = item.IsDisabled;
        const isHasPrice = item.Service.Price;
        const isSkippedAutoPayment = isBefore(realExpireDate, toDay);
        return !isActive && !isDisabled && isHasPrice && isSkippedAutoPayment;
      });
    }
    return [];
  };

  const mapAccount = (
    accounts: CtvAccount[] | IptvAccount[] | UnionAccount[] | InternetAccount[],
    type: AccountTypes,
  ) => {
    return accounts.map((account) => {
      return {
        name: account.Service.Name,
        price: account.Service.Price,
        isDaily: account.Service.Monthly,
        iconType: getCardIconType(type, account.Service.Type?.id),
        handleActivate,
        accountId: account.id,
        accountType: type,
      };
    });
  };

  useEffect(() => {
    setAccountsToActivate([]);
    if (connectedAccounts) {
      if (connectedAccounts.ctv_accounts) {
        const notActivatedAccounts = filterNotActivatedAccounts(connectedAccounts.ctv_accounts);
        setAccountsToActivate((prevState) => [...prevState, ...mapAccount(notActivatedAccounts, AccountTypes.Ctv)]);
      }
      if (connectedAccounts.iptv_accounts) {
        const notActivatedAccounts = filterNotActivatedAccounts(connectedAccounts.iptv_accounts);
        setAccountsToActivate((prevState) => [...prevState, ...mapAccount(notActivatedAccounts, AccountTypes.Iptv)]);
      }
      if (connectedAccounts.union_accounts) {
        const notActivatedAccounts = filterNotActivatedAccounts(connectedAccounts.union_accounts);
        setAccountsToActivate((prevState) => [...prevState, ...mapAccount(notActivatedAccounts, AccountTypes.Union)]);
      }
      if (connectedAccounts.internet_accounts) {
        const notActivatedAccounts = filterNotActivatedAccounts(connectedAccounts.internet_accounts);
        setAccountsToActivate((prevState) => [
          ...prevState,
          ...mapAccount(notActivatedAccounts, AccountTypes.Internet),
        ]);
      }
    }
  }, [connectedAccounts]);

  useEffect(() => {
    onLoad();
    return () => {
      localStorage.removeItem(LocalStorageItems.TopUpAmount);
      localStorage.removeItem(LocalStorageItems.TopUpBalance);

      const pusher = Wss.getInstance();
      if (channelRef.current) {
        channelRef.current.unbind_all();
      }
      if (channelNameRef.current) {
        pusher.unsubscribe(channelNameRef.current);
      }
      channelRef.current = null;
      channelNameRef.current = null;
    };
  }, []);

  const navigateToMainPage = () => {
    history.push(GlobalRouteNames.HomePage);
  };

  const navigateToPaymentHistoryPage = () => {
    history.push(GlobalRouteNames.PaymentHistory);
  };

  const getTableAccounts = (accounts: accountsToActivate[]): TableBodyCell[] => {
    return accounts.map((el) => {
      return {
        id: el.accountId,
        bodyCells: [
          <div className="icon-container">
            <AccountIcon accountType={el.iconType} />
            {el.name}
          </div>,
          <TextButton
            label={t('buttons.activate')}
            className={TextButtonClassNames.PrimaryActive}
            handleClick={() => handleActivate(el.accountId, el.accountType)}
          />,
        ],
      };
    });
  };

  if (processing) {
    return <OrderProcessing />;
  }

  return (
    <div className="success-container">
      <h1>{t('success.balanceHasBeenReplenishedSuccessfully')}</h1>
      <p className="success-text">{t('success.yourBalanceHasBeenReplenishedByUah', { val: amount || topUpAmount })}</p>
      {!!accountsToActivate.length && (
        <div className="accounts-container">
          <div className="accounts-container__header">
            <hr />
            <p className="accounts-container__header__title">{t('success.servicesPendingActivation')}</p>
          </div>
          <BasicTable tableHead={['', '']} tableBodyCells={getTableAccounts(accountsToActivate)} />
        </div>
      )}
      <div className="success-container__buttons-group">
        <CustomMUIButton
          label={t('buttons.toMain')}
          handleClick={navigateToMainPage}
          className={ButtonClassNames.SecondaryOutlined}
          isBold
        />
        <CustomMUIButton
          label={t('buttons.yourPayments')}
          handleClick={navigateToPaymentHistoryPage}
          className={ButtonClassNames.PrimaryFulfilled}
          isBold
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
